import { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Download, Loader2, CheckCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PDFDownloadModal = ({ open, onOpenChange, pdfUrls }) => {
  const { language, t } = useLanguage();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error(t('Vennligst oppgi en gyldig e-postadresse', 'Please enter a valid email address'));
      return;
    }

    setIsSubmitting(true);

    try {
      // Send email to backend
      await axios.post(`${API}/cv-download`, {
        email,
        language,
        timestamp: new Date().toISOString()
      });

      setIsConfirmed(true);
      toast.success(t(
        'E-post bekreftet! CV lastes ned.',
        'Email confirmed! CV downloading.'
      ));

      // Trigger download after short delay
      setTimeout(() => {
        const pdfUrl = language === 'no' ? pdfUrls.no : pdfUrls.en;
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `Jakob_Bronebakk_CV_${language.toUpperCase()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Reset and close modal
        setTimeout(() => {
          setEmail("");
          setIsConfirmed(false);
          onOpenChange(false);
        }, 1000);
      }, 500);

    } catch (error) {
      console.error('Error submitting email:', error);
      toast.error(t('Noe gikk galt. Prøv igjen.', 'Something went wrong. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pdf-download-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-slate-900">
            {t('Last ned CV', 'Download CV')}
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {t(
              'Oppgi e-postadressen din for å laste ned CV-en.',
              'Enter your email address to download the CV.'
            )}
          </DialogDescription>
        </DialogHeader>

        {isConfirmed ? (
          <div className="flex flex-col items-center py-8">
            <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
            <p className="text-slate-700 font-medium">
              {t('Nedlasting startet!', 'Download started!')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">
                {t('E-postadresse', 'Email Address')}
              </Label>
              <Input
                id="email"
                type="email"
                data-testid="email-input"
                placeholder={t('din@epost.no', 'your@email.com')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-slate-300 focus:border-sky-500 focus:ring-sky-500"
                required
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
              >
                {t('Avbryt', 'Cancel')}
              </Button>
              <Button
                type="submit"
                data-testid="submit-email-btn"
                disabled={isSubmitting}
                className="flex-1 bg-sky-600 hover:bg-sky-500 text-white"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {t('Last ned', 'Download')}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PDFDownloadModal;
